import { FC, useState, useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'jbc-front/components/Button';
import { Input } from 'jbc-front/components/presenters/form/Input';
import { Content } from '@/components/layout/Content';
import { displayFormat, fullNameFormat } from '@/components/utils/formatter';
import { FormErrors } from '@/components/form/FormErrors';
import { Headding, Item, Panel } from '@/components/PrimaryPanel';
import { useSession } from '@/components/SessionProvider';
import { useLazyQuery } from '@/hooks/graphql/useQuery';
import { useMutation } from '@/hooks/graphql/useMutation';
import { useNotify } from '@/hooks/useNotify';
import { parseBackUrl } from '@/utils/url';
import { PermissionForm } from '../edit/parts/PermissionForm/PermissionForm';
import { RoleProhibit } from '../edit/parts/PermissionForm/roleOptions';
import {
  ADD_LMS_USER,
  AddLmsUserQueryVariables,
  LmsUser,
  SEARCH_LMS_USERS,
  SearchLmsUsersQueryResult,
  SearchLmsUsersQueryVariables
} from './query';
import { Schema, schema } from './schema';
import styles from './InviteUser.scss';
import { FilterTag } from 'jbc-front/components/presenters/ui/FilterTag';
import { Modal } from 'jbc-front/components/presenters/ui/Modal';

export const InviteUser: FC = () => {
  const { user: currentUser } = useSession();
  const notify = useNotify();

  const location = useLocation();
  const history = useHistory();

  const [identifier, setIdentifier] = useState('');
  const [searchOptions, setSearchOptions] = useState({
    searchName: true,
    searchEmail: true,
    searchStaffCode: true
  });

  const placeholderText = useMemo(() => {
    const selected: string[] = [];
    if (searchOptions.searchName) selected.push('氏名');
    if (searchOptions.searchEmail) selected.push('メールアドレス');
    if (searchOptions.searchStaffCode) selected.push('スタッフコード');

    if (selected.length === 0) {
      return '検索対象を選択してください';
    }

    return selected.join('、');
  }, [searchOptions]);

  const methods = useForm<Schema>({
    resolver: yupResolver(schema),
    context: { currentUser }
  });
  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting }
  } = methods;

  const [searchLmsUsers, { data: lmsUsersData, loading: lmsUserLoading }] = useLazyQuery<
    SearchLmsUsersQueryResult,
    SearchLmsUsersQueryVariables
  >(SEARCH_LMS_USERS, {
    fetchPolicy: 'no-cache'
  });
  const lmsUsers = lmsUsersData?.lmsUsersSearch;

  const [selectedLmsUser, setSelectedLmsUser] = useState<LmsUser | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [addLmsUser] = useMutation<unknown, AddLmsUserQueryVariables>(ADD_LMS_USER);

  const handleClickSearch = async () => {
    if (searchOptions.searchName || searchOptions.searchEmail || searchOptions.searchStaffCode) {
      if (identifier) {
        try {
          await searchLmsUsers({
            variables: {
              keyword: identifier,
              options: searchOptions
            }
          });
          setSelectedLmsUser(null);
          setIsModalOpen(true);
        } catch (err) {
          if (err instanceof Error) {
            notify(err.message, 'error');
          }
        }
      } else {
        notify('メールアドレスまたはスタッフコードを入れてください', 'error');
      }
    } else {
      notify('検索対象を選択してください', 'error');
    }
  };

  const onSubmit: SubmitHandler<Schema> = async data => {
    if (!selectedLmsUser?.email) {
      notify('管理者を選択してください', 'error');
      return;
    }

    const roleCodes = [data.stressCheckRole, data.healthCheckRole, data.userManageRole].filter(
      (role): role is string => role !== undefined && role !== null && role !== RoleProhibit
    );
    if (roleCodes.length === 0) {
      setError('root.roleCodes', {
        type: 'manual',
        message: 'すべての権限を「権限なし」にはできません。少なくとも1つ以上の権限を「権限あり」に設定してください。'
      });
      return;
    }

    const input = {
      email: selectedLmsUser.email,
      name: selectedLmsUser.name || '',
      roleCodes,
      employee: selectedLmsUser.employee && {
        commonId: selectedLmsUser.employee.commonId,
        officeId: selectedLmsUser.employee.officeId,
        staffCode: selectedLmsUser.employee.staffCode,
        email: selectedLmsUser.email,
        firstName: selectedLmsUser.employee.firstName,
        lastName: selectedLmsUser.employee.lastName,
        firstNameKana: selectedLmsUser.employee.firstNameKana,
        lastNameKana: selectedLmsUser.employee.lastNameKana
      }
    };

    try {
      await addLmsUser({ variables: { input } });
      notify('招待メールを送信しました', 'success');
      history.push(parseBackUrl(location, '/settings/users'));
    } catch (err: any) {
      const message = err.graphQLErrors?.[0]?.message || '不明なエラーが発生しました。';
      setError('root.serverError', {
        type: 'manual',
        message: message
      });
    }
  };

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">ジョブカン労務HRから管理者を追加</h1>
      </div>
      <Content size="m">
        <FormProvider {...methods}>
          {errors.root?.roleCodes?.message && (
            <FormErrors errors={[{ path: 'roleCodes', message: errors.root.roleCodes.message }]} />
          )}

          <Panel>
            <Headding>管理者を追加</Headding>
            <Item>
              <div className={styles.searchContainer}>
                <div className={styles.searchForm}>
                  <Input
                    className={styles.identifier}
                    placeholder={placeholderText}
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                  />
                  <div className={styles.searchField}>
                    <Button
                      className={styles.searchButton}
                      primary
                      onClick={handleClickSearch}
                      disabled={!identifier || lmsUserLoading || isSubmitting}>
                      検索
                    </Button>
                  </div>
                </div>
                <div className={styles.searchOptions}>
                  <FilterTag
                    isActive={searchOptions.searchName}
                    onClick={() => setSearchOptions({ ...searchOptions, searchName: !searchOptions.searchName })}>
                    氏名
                  </FilterTag>
                  <FilterTag
                    isActive={searchOptions.searchEmail}
                    onClick={() => setSearchOptions({ ...searchOptions, searchEmail: !searchOptions.searchEmail })}>
                    メールアドレス
                  </FilterTag>
                  <FilterTag
                    isActive={searchOptions.searchStaffCode}
                    onClick={() =>
                      setSearchOptions({ ...searchOptions, searchStaffCode: !searchOptions.searchStaffCode })
                    }>
                    スタッフコード
                  </FilterTag>
                </div>
              </div>
              {selectedLmsUser && (
                <div className="l-overflow-scroll">
                  <table className={styles.table}>
                    <thead>
                      <tr className={styles.header}>
                        <th>氏名</th>
                        <th>メールアドレス</th>
                        <th>スタッフコード</th>
                        <th>労務権限</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          {selectedLmsUser.employee
                            ? fullNameFormat(selectedLmsUser.employee?.lastName, selectedLmsUser.employee?.firstName)
                            : displayFormat(selectedLmsUser.name)}
                        </td>
                        <td>{displayFormat(selectedLmsUser.email)}</td>
                        <td>
                          {selectedLmsUser.employee
                            ? displayFormat(selectedLmsUser.employee?.staffCode)
                            : '従業員登録なし'}
                        </td>
                        <td>{displayFormat(selectedLmsUser.clientRole)}</td>
                        <td>
                          {selectedLmsUser && selectedLmsUser.email === selectedLmsUser.email ? (
                            <Button className={styles.selectButton} onClick={() => setSelectedLmsUser(null)}>
                              選択を解除
                            </Button>
                          ) : (
                            <Button
                              className={styles.selectButton}
                              primary
                              onClick={() => {
                                setSelectedLmsUser(selectedLmsUser);
                              }}>
                              選択
                            </Button>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              <PermissionForm />
            </Item>
          </Panel>

          <div className={styles.footer}>
            <Button as={Link} to={parseBackUrl(location, '/settings/users')}>
              キャンセル
            </Button>
            <Button
              primary
              onClick={handleSubmit(onSubmit)}
              disabled={!selectedLmsUser?.email || lmsUserLoading || isSubmitting}>
              追加
            </Button>
          </div>
        </FormProvider>
      </Content>

      <Modal className={styles.modal} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Modal.Header onClose={() => setIsModalOpen(false)}>検索結果</Modal.Header>
        <Modal.Body>
          <div className="l-overflow-scroll">
            {lmsUserLoading ? (
              <p>検索中...</p>
            ) : (
              <>
                {!lmsUsers || lmsUsers.length === 0 ? (
                  <p>該当するユーザーが見つかりませんでした。</p>
                ) : (
                  <table className={styles.table}>
                    <thead>
                      <tr className={styles.header}>
                        <th>氏名</th>
                        <th>メールアドレス</th>
                        <th>スタッフコード</th>
                        <th>労務権限</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedLmsUser ? [selectedLmsUser] : lmsUsers || []).map(user => (
                        <tr key={user.email}>
                          <td>
                            {user.employee
                              ? fullNameFormat(user.employee?.lastName, user.employee?.firstName)
                              : displayFormat(user.name)}
                          </td>
                          <td>{displayFormat(user.email)}</td>
                          <td>{user.employee ? displayFormat(user.employee?.staffCode) : '従業員登録なし'}</td>
                          <td>{displayFormat(user.clientRole)}</td>
                          <td>
                            {selectedLmsUser && selectedLmsUser.email === user.email ? (
                              <Button className={styles.selectButton} onClick={() => setSelectedLmsUser(null)}>
                                選択解除
                              </Button>
                            ) : (
                              <Button
                                className={styles.selectButton}
                                primary
                                onClick={() => {
                                  setSelectedLmsUser(user);
                                  setIsModalOpen(false);
                                }}>
                                選択
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setIsModalOpen(false)}>閉じる</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
